(() => {
  if (window.__variantLogoFilterLoaded) return;
  window.__variantLogoFilterLoaded = true;

  const PICKER_SELECTOR = 'variant-picker[data-logo-filter]';
  const initializedPickers = new WeakSet();
  const pickerStates = new WeakMap();
  const scheduledPickers = new Set();

  let updateScheduled = false;

  function getOptionName(control) {
    if (control instanceof HTMLSelectElement) {
      const selectedOption = control.selectedOptions[0];

      if (selectedOption?.dataset.optionName) {
        return selectedOption.dataset.optionName.trim().toLowerCase();
      }

      return control.name
        .match(/^options\[(.*)\]$/)?.[1]
        ?.trim()
        .toLowerCase();
    }

    return control.dataset.optionName?.trim().toLowerCase();
  }

  function findSelect(picker, name) {
    return Array.from(
      picker.querySelectorAll('select[name^="options["]')
    ).find((select) => getOptionName(select) === name);
  }

  function findSelectedControl(picker, name) {
    const select = findSelect(picker, name);

    if (select) return select;

    return Array.from(
      picker.querySelectorAll('input[data-option-name]:checked')
    ).find((input) => getOptionName(input) === name);
  }

  function getOptionPosition(control) {
    if (control.dataset.optionPosition !== undefined) {
      const position = Number(control.dataset.optionPosition);

      if (!Number.isNaN(position)) return position;
    }

    /*
     * Horizon's data-input-id is formatted as:
     * "[one-based option position]-[value index]"
     */
    const inputId =
      control instanceof HTMLSelectElement
        ? control.selectedOptions[0]?.dataset.inputId
        : control.dataset.inputId;

    const oneBasedPosition = Number(inputId?.split('-')[0]);

    return Number.isNaN(oneBasedPosition)
      ? Number.NaN
      : oneBasedPosition - 1;
  }

  function getVariantData(picker) {
    const dataElement = picker.querySelector(
      '[data-logo-filter-variants]'
    );

    if (!dataElement) return null;

    try {
      return {
        combinations: JSON.parse(dataElement.textContent),
        signature: dataElement.textContent.trim(),
      };
    } catch (error) {
      console.error('Unable to parse Logo variant combinations.', error);
      return null;
    }
  }

  function getMasterLogoOptions(
    picker,
    logoSelect,
    combinationSignature
  ) {
    const currentOptions = Array.from(logoSelect.options);
    const currentState = pickerStates.get(picker);

    /*
     * Horizon restores the complete option list when it morphs the picker.
     * Capture that refreshed list, including its updated Shopify data.
     */
    if (
      !currentState ||
      currentState.signature !== combinationSignature ||
      currentOptions.length >= currentState.logoOptions.length
    ) {
      const nextState = {
        signature: combinationSignature,
        logoOptions: currentOptions.map((option) =>
          option.cloneNode(true)
        ),
      };

      pickerStates.set(picker, nextState);
      return nextState.logoOptions;
    }

    return currentState.logoOptions;
  }

  function setSelectedValue(select, value) {
    select.value = value;

    Array.from(select.options).forEach((option) => {
      const selected = option.value === value;

      option.selected = selected;
      option.toggleAttribute('selected', selected);
    });
  }

  function filterLogoOptions(
    picker,
    { notifyTheme = false } = {}
  ) {
    const colorControl = findSelectedControl(picker, 'color');
    const logoSelect = findSelect(picker, 'logo');

    if (!colorControl || !logoSelect) return;

    const colorPosition = getOptionPosition(colorControl);
    const logoPosition = getOptionPosition(logoSelect);

    if (
      Number.isNaN(colorPosition) ||
      Number.isNaN(logoPosition)
    ) {
      return;
    }

    const variantData = getVariantData(picker);

    if (!variantData) return;

    const masterOptions = getMasterLogoOptions(
      picker,
      logoSelect,
      variantData.signature
    );

    const selectedColor = colorControl.value;
    const previousLogo = logoSelect.value;

    /*
     * This checks whether a variant exists. It deliberately does not use
     * variant.available, so an existing sold-out combination remains visible.
     */
    const validLogos = new Set(
      variantData.combinations
        .filter(
          (options) =>
            String(options[colorPosition]) === selectedColor
        )
        .map((options) => String(options[logoPosition]))
    );

    const desiredOptions = masterOptions.filter((option) =>
      validLogos.has(option.value)
    );

    const nextLogo = validLogos.has(previousLogo)
      ? previousLogo
      : desiredOptions[0]?.value;

    if (!nextLogo) {
      logoSelect.replaceChildren();
      logoSelect.disabled = true;
      return;
    }

    const currentValues = Array.from(
      logoSelect.options,
      (option) => option.value
    );

    const desiredValues = desiredOptions.map(
      (option) => option.value
    );

    const optionsChanged =
      currentValues.length !== desiredValues.length ||
      currentValues.some(
        (value, index) => value !== desiredValues[index]
      );

    if (optionsChanged) {
      const replacements = desiredOptions.map((option) => {
        const copy = option.cloneNode(true);
        const selected = copy.value === nextLogo;

        /*
         * Remove the old "Unavailable" suffix and any invalid-combination
         * state inherited from Horizon's previous selection.
         */
        copy.textContent = copy.value;
        copy.hidden = false;
        copy.disabled = false;
        copy.removeAttribute('hidden');
        copy.removeAttribute('disabled');
        copy.selected = selected;
        copy.toggleAttribute('selected', selected);

        return copy;
      });

      logoSelect.replaceChildren(...replacements);
    } else {
      Array.from(logoSelect.options).forEach((option) => {
        const selected = option.value === nextLogo;

        if (option.textContent.trim() !== option.value) {
          option.textContent = option.value;
        }

        option.hidden = false;
        option.disabled = false;
        option.selected = selected;
        option.toggleAttribute('selected', selected);
      });
    }

    logoSelect.disabled = false;
    setSelectedValue(logoSelect, nextLogo);

    if (notifyTheme && nextLogo !== previousLogo) {
      queueMicrotask(() => {
        if (logoSelect.isConnected) {
          logoSelect.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      });
    }
  }

  function scheduleFilter(picker) {
    scheduledPickers.add(picker);

    if (updateScheduled) return;
    updateScheduled = true;

    queueMicrotask(() => {
      updateScheduled = false;

      scheduledPickers.forEach((picker) => {
        if (picker.isConnected) {
          filterLogoOptions(picker);
        }
      });

      scheduledPickers.clear();
    });
  }

  function initializePicker(picker) {
    if (initializedPickers.has(picker)) return;

    initializedPickers.add(picker);

    /*
     * Supports both Color dropdowns and Color radio/swatches.
     * Capture phase updates Logo before Horizon handles the same event.
     */
    picker.addEventListener(
      'change',
      (event) => {
        const target = event.target;

        if (
          !(
            target instanceof HTMLSelectElement ||
            target instanceof HTMLInputElement
          )
        ) {
          return;
        }

        if (getOptionName(target) !== 'color') return;

        filterLogoOptions(picker);
      },
      true
    );

    filterLogoOptions(picker, { notifyTheme: true });
  }

  function scanForPickers(root) {
    if (
      root instanceof Element &&
      root.matches(PICKER_SELECTOR)
    ) {
      initializePicker(root);
    }

    root
      .querySelectorAll?.(PICKER_SELECTOR)
      .forEach(initializePicker);
  }

  customElements.whenDefined('variant-picker').then(() => {
    scanForPickers(document);

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.target instanceof Element) {
          const picker = record.target.closest(PICKER_SELECTOR);

          if (picker) scheduleFilter(picker);
        }

        record.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            scanForPickers(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });
  });
})();